// Shared "this is interactive" hover cursor for Konva nodes. Hovering a wired
// node sets the stage-container cursor (e.g. a hand for board objects) and
// resets it on leave, so the whole board signals interactivity consistently.
// Reusable so every object — and anything added later — behaves the same.

import Konva from 'konva';

/**
 * Dauer der Hover-Animation, in SEKUNDEN (Konvas Einheit fuer `to()` — nicht
 * Millisekunden wie in CSS).
 *
 * Hin- und Rueckweg sind verschieden lang: Beim Verlassen laeuft es etwas
 * laenger aus, das nimmt der Bewegung die Haerte, wenn die Maus ueber mehrere
 * Anfasser streift. Dieselbe Asymmetrie wie beim Optionskreis.
 */
const HOVER_ANIM_IN_SECONDS = 0.5;
const HOVER_ANIM_OUT_SECONDS = 0.6;

/**
 * Wie stark ein Anfasser beim Darueberfahren waechst. Konva skaliert um den
 * Ursprung des Knotens, und Anfasser tragen `offsetX/offsetY = Groesse / 2` —
 * der Ursprung IST also ihre Mitte, sie wachsen von selbst mittig.
 */
const HOVER_ANIM_SCALE = 1.15;

/**
 * Show `cursor` while the pointer is over `node`, but only when `enabled()`
 * returns true (e.g. only in select mode). The cursor is reset on leave
 * regardless, so a mode change while hovering can't strand a stale cursor.
 *
 * Uses a namespaced event so it can be detached with `node.off('.hovercursor')`
 * without touching other listeners.
 *
 * Anke, 2026-09-12 — re-applies on `mousemove` too, not just `mouseenter`:
 * "wenn ich [...] vor dem zweiten Klick die Maus nicht nach ausserhalb der
 * Textbox bewege, dann schaltet der Cursor nicht auf das Text-Icon um" — the
 * pointer entered BEFORE the first click selected the textbox (so `enabled()`
 * was false at that `mouseenter`), and without a leave+re-enter, `mouseenter`
 * never fires again to re-check it once selection changes. `mousemove` fires
 * on every tick the pointer sits over the node — cheap here (one property
 * write, usually to its already-current value) and it's what actually keeps
 * the cursor honest for the whole hover, not just its start.
 *
 * `enabled` receives the triggering event so a caller can look at
 * `evt.target` — the ACTUAL shape under the pointer, not `node` itself:
 * Konva keeps `evt.target` fixed to the originally hit shape as the event
 * bubbles up through ancestors (`Node._fireAndBubble` only overwrites it for
 * a `Shape`, never for a `Group`), so a wiring on a parent group can still
 * tell whether the pointer is over a specific child (Anke, 2026-09-12 — the
 * textbox's overset "+" indicator has its OWN 'pointer' cursor via
 * `wireHoverFillGroup`; the textbox group's own 'text'-cursor rule must
 * yield to it while hovering that child specifically).
 */
export function wireHoverCursor(
  node: Konva.Node,
  cursor: string,
  enabled: (evt: Konva.KonvaEventObject<MouseEvent>) => boolean,
): void {
  const applyIfEnabled = (evt: Konva.KonvaEventObject<MouseEvent>): void => {
    if (!enabled(evt)) return;
    const container = node.getStage()?.container();
    if (container) container.style.cursor = cursor;
  };
  node.on('mouseenter.hovercursor mousemove.hovercursor', applyIfEnabled);
  node.on('mouseleave.hovercursor', () => {
    const container = node.getStage()?.container();
    if (container) container.style.cursor = '';
  });
}

/**
 * Fill a handle with `getFill()` while hovered and restore `restingFill` on
 * leave; optionally also set a cursor. `getFill` is read live so a themed
 * colour (e.g. from a CSS variable) can change without re-wiring. Uses a
 * namespaced event so it can be detached with `handle.off('.hoverfill')` and
 * re-wired safely (handles that Konva rebuilds, like transformer anchors).
 *
 * Anke, 2026-07-16: Farbe UND Groesse laufen weich (siehe HOVER_ANIM_*), statt
 * zu springen. Dieselbe Sprache wie im Optionskreis — dort waechst der Knopf
 * beim Darueberfahren ebenfalls leicht und hellt auf.
 *
 * Die Animation liegt bewusst HIER und nicht bei den einzelnen Anfassern: Ueber
 * diese Funktion laufen die Eck- und Dreh-Anfasser des Transformers genauso wie
 * die Verbinder-Anfasser. Eine Stelle, ein Verhalten.
 */
export function wireHoverFill(
  handle: Konva.Shape,
  getFill: () => string,
  restingFill: string,
  cursor?: string,
): void {
  handle.off('.hoverfill');

  const setCursor = (value: string): void => {
    if (!cursor) return;
    const container = handle.getStage()?.container();
    if (container) container.style.cursor = value;
  };

  // `to()` legt einen Konva-Tween an, der die Ebene selbst neu zeichnet — ein
  // eigenes batchDraw waere doppelt gemoppelt. Faehrt die Maus schnell rein und
  // raus, raeumt Konva den laufenden Tween derselben Eigenschaft von sich aus
  // weg (Tween._addAttr), es koennen sich also keine zwei um dieselbe Farbe
  // streiten.
  // Farbe und Groesse laufen mit derselben Kurve, aber als ZWEI Tweens (Konva
  // fuehrt seine Tween-Liste je Eigenschaft; zwei Tweens auf einem Knoten
  // stoeren sich nicht, solange sie verschiedene Eigenschaften anfassen).
  //
  // Anke, 2026-07-16: "es ruckt am Anfang" — daher EaseInOut. Vorher liefen
  // hier ausrollende Kurven (StrongEaseOut / BackEaseOut). Die starten mit
  // VOLLER Geschwindigkeit und werden erst zum Ende hin langsam: Der Einsatz
  // ist ein Ruck, und laengere Dauern verlaengern nur den Nachlauf, statt den
  // Ruck zu nehmen. EaseInOut schleicht an, laeuft durch und rollt aus — an
  // beiden Enden weich.
  //
  // Damit faellt auch das leichte Ueberschwingen der Groesse weg: Es kam von
  // BackEaseOut, und eine Kurve, die federt, kann nicht sanft einsetzen. Weich
  // schlaegt hier lebendig — genau darum ging es.
  const animate = (fill: string, scale: number, seconds: number): void => {
    handle.to({ fill, duration: seconds, easing: Konva.Easings.EaseInOut });
    handle.to({ scaleX: scale, scaleY: scale, duration: seconds, easing: Konva.Easings.EaseInOut });
  };

  handle.on('mouseenter.hoverfill', () => {
    setCursor(cursor ?? '');
    animate(getFill(), HOVER_ANIM_SCALE, HOVER_ANIM_IN_SECONDS);
  });

  handle.on('mouseleave.hoverfill', () => {
    setCursor('');
    animate(restingFill, 1, HOVER_ANIM_OUT_SECONDS);
  });
}

/** One recolouring part of a composite handle (see {@link wireHoverFillGroup}). */
export interface HoverFillPart {
  readonly shape: Konva.Shape;
  /** Fill while hovered — read lazily so a themed colour stays live. */
  readonly hover: () => string;
  /** Fill at rest. */
  readonly resting: string;
}

/**
 * Hover for a COMPOSITE handle: a group that grows as one while its parts
 * recolour independently.
 *
 * Anke, 2026-07-18 — needed for the textbox overset "+" marker, which is a box
 * plus a glyph. `wireHoverFill` animates fill and scale on ONE shape; scaling
 * only the box there would leave the glyph behind, and a single fill colour
 * cannot serve both (the glyph has to invert to stay readable once the box
 * darkens).
 *
 * Deliberately lives HERE beside `wireHoverFill`, sharing its curve, timings and
 * scale factor — the point of this module is that every handle on the board
 * moves in the same language. The group must carry `offset = size / 2` so Konva
 * scales it about its own centre, exactly as the square handles do.
 */
export function wireHoverFillGroup(
  group: Konva.Group,
  parts: ReadonlyArray<HoverFillPart>,
  cursor?: string,
): void {
  group.off('.hoverfill');

  const setCursor = (value: string): void => {
    if (!cursor) return;
    const container = group.getStage()?.container();
    if (container) container.style.cursor = value;
  };

  const animate = (hovered: boolean, seconds: number): void => {
    const scale = hovered ? HOVER_ANIM_SCALE : 1;
    group.to({ scaleX: scale, scaleY: scale, duration: seconds, easing: Konva.Easings.EaseInOut });
    for (const part of parts) {
      part.shape.to({
        fill: hovered ? part.hover() : part.resting,
        duration: seconds,
        easing: Konva.Easings.EaseInOut,
      });
    }
  };

  group.on('mouseenter.hoverfill', () => {
    setCursor(cursor ?? '');
    animate(true, HOVER_ANIM_IN_SECONDS);
  });

  group.on('mouseleave.hoverfill', () => {
    setCursor('');
    animate(false, HOVER_ANIM_OUT_SECONDS);
  });
}
